import type { Gloss, InstantLookup } from '@nelnela/contracts';
import { api } from './api';

/**
 * The instant layer, client side (M10-i i-1).
 *
 * Three screens open the gloss sheet on a tap — Watch, Read and Chat — and all three have the
 * same shape: open immediately, show a skeleton, wait ~5 s for one Haiku call. This is the part
 * that fills the skeleton in one round trip instead, out of what the database already knows:
 * the cached lemma, its catalog entry, and the last time this surface was glossed in some other
 * sentence.
 *
 * WHAT REPLACES WHAT, because the whole design is in this sentence: the Haiku gloss replaces the
 * provisional MEANING; the dictionary facts — lemma, part of speech, level, whether a forms
 * panel exists — STAY. A learner reading a provisional meaning and then seeing it swapped for a
 * better one has lost nothing; a learner watching the level chip flicker away and back has
 * watched the app change its mind about a fact.
 *
 * …unless the lemma itself disagrees, which is the one case where the dictionary facts were
 * about the wrong word. Then the whole block is re-resolved — see `reconcile`.
 *
 * SESSION-SCOPED MEMO, mirroring the gloss memo each screen already keeps. The instant answer for
 * a (selection, sentence) cannot change within a session in any way the learner would see: the
 * only writer is the gloss path, and a gloss that lands mid-session writes the row this lookup
 * would have read. Re-asking would cost a round trip to learn what is already on screen.
 */

const memo = new Map<string, Promise<InstantLookup>>();

/** The memo key. `\u0000` as the escape, never the literal byte — a raw NUL makes the file binary
 *  to git and grep, silently (worker/test/source-hygiene.test.ts records it happening three times). */
export function instantKey(selection: string, sentence: string | undefined): string {
  return `${selection}\u0000${sentence ?? ''}`;
}

/**
 * Ask the instant route. NEVER REJECTS: this is a decoration on a path that already works, so a
 * failure means the sheet shows the skeleton it would have shown anyway. A rejected promise here
 * would have to be caught at three call sites to achieve exactly that.
 */
export function instantLookup(
  selection: string,
  sentence: string | undefined,
  deps: { api: Pick<typeof api, 'lookupInstant'> } = { api },
): Promise<InstantLookup> {
  const key = instantKey(selection, sentence);
  const hit = memo.get(key);
  if (hit) return hit;
  const p = deps.api
    .lookupInstant({ selection, ...(sentence ? { sentence } : {}) })
    .catch(() => ({}) as InstantLookup);
  memo.set(key, p);
  return p;
}

/** Tests only — the memo is module state and would otherwise leak between cases. */
export function resetInstantMemo(): void {
  memo.clear();
}

/**
 * What the sheet shows before the model answers: the provisional meaning and where it came from.
 *
 * Two sources, in this order, and the order is a judgement about which is more likely to be right
 * for THIS sentence:
 *
 *   1. **A prior gloss of the same surface.** Grounded in a real sentence the learner actually
 *      read, produced by the same model that is about to answer. Its sentence was a different
 *      one, which is exactly what `source: 'gloss'` warns about.
 *   2. **The catalog gloss.** Reviewed by hand and correct about the LEMMA, but sense-blind: it
 *      is the dictionary headword's meaning, not this occurrence's.
 *
 * Returns null when there is nothing to show, which is the first tap on an unknown word — the
 * skeleton stays and nothing was promised.
 */
export function provisionalMeaning(
  instant: InstantLookup | null | undefined,
): { text: string; source: 'gloss' | 'catalog'; sentence?: string } | null {
  if (!instant) return null;
  if (instant.priorGloss?.meaning) {
    return {
      text: instant.priorGloss.meaning,
      source: 'gloss',
      sentence: instant.priorGloss.sentence,
    };
  }
  if (instant.catalog?.gloss) return { text: instant.catalog.gloss, source: 'catalog' };
  return null;
}

/**
 * True when the model's lemma contradicts the provisional block's.
 *
 * Undefined on either side is not a disagreement: with no cached lemma there was no claim to
 * contradict, and a gloss with no lemma has not made one.
 */
export function lemmaDisagrees(
  instant: InstantLookup | null | undefined,
  gloss: Gloss | null | undefined,
): boolean {
  const was = instant?.lemma;
  const now = gloss?.response?.lemma;
  return Boolean(was && now && was !== now);
}

/**
 * The instant answer as it should be SHOWN beside a given gloss — reconciled if the model
 * disagreed about the lemma, unchanged otherwise.
 *
 * Both places that put an instant answer on the sheet go through this, and that symmetry is the
 * point. The first version reconciled only in the gloss handler, which meant the SESSION-MEMO
 * path skipped it entirely: tap a word, tap it again in the same sentence, and the second tap
 * short-circuits the gloss request (`|| memo`) — so the dictionary block was painted from the
 * cached lemma with no check against the gloss already on screen. That is exactly the mixed sheet
 * `reconcile` exists to prevent, arriving through the one door it was not watching.
 */
export async function settle(
  instant: InstantLookup,
  gloss: Gloss | null | undefined,
  deps: { api: Pick<typeof api, 'catalogLookup'> } = { api },
): Promise<InstantLookup> {
  if (!gloss || !lemmaDisagrees(instant, gloss)) return instant;
  return reconcile(instant, gloss, deps);
}

/**
 * The re-resolve. When Haiku's lemma disagrees, swap the WHOLE dictionary block to the new lemma
 * — level chip, forms affordance and all.
 *
 * Half a sheet about lemma X sitting under a meaning for lemma Y is worse than a late repaint:
 * the learner has no way to tell which half is about the word they tapped, and the level chip is
 * the one thing on the sheet they are most likely to take on trust.
 *
 * This is `catalogLookup`'s first caller. The route has existed end to end since M4a and no
 * screen has ever called it (M10 handoff § the state of the code); this is the question it was
 * built to answer.
 *
 * Returns the ORIGINAL instant on failure or on a lemma the catalog does not know, minus the
 * dictionary facts that are now known to be about the wrong word — keeping them would be the
 * exact mixed sheet this function exists to prevent.
 */
export async function reconcile(
  instant: InstantLookup | null | undefined,
  gloss: Gloss,
  deps: { api: Pick<typeof api, 'catalogLookup'> } = { api },
): Promise<InstantLookup> {
  const lemma = gloss.response?.lemma;
  const kept: InstantLookup = {
    ...(instant?.priorGloss ? { priorGloss: instant.priorGloss } : {}),
  };
  if (!lemma) return kept;
  try {
    const entry = await deps.api.catalogLookup(lemma);
    if (!entry) return { ...kept, lemma };
    return {
      ...kept,
      lemma,
      pos: entry.pos,
      senseIndex: entry.senseIndex,
      catalog: entry,
      // Deliberately NOT carried over from the old block: it was a fact about a different
      // entry. Unknown reads as absent, and the forms affordance stays hidden until the next
      // instant lookup of this surface — which, thanks to the gloss's own lemma_cache write,
      // now resolves to the right lemma.
    };
  } catch {
    return { ...kept, lemma };
  }
}
